import React, { Suspense, createContext, useState, useContext, useEffect, useCallback, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { api } from './lib/api';
import { Layout } from './components/Layout';
import { GlobalProcessing } from './components/GlobalProcessing';
import { ToastProvider } from './components/Toast';

const LoginPage = lazy(() =>
  import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const MapPage = lazy(() => import('./pages/MapPage').then((m) => ({ default: m.MapPage })));
const TimeOffPage = lazy(() =>
  import('./pages/TimeOffPage').then((m) => ({ default: m.TimeOffPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TechniciansPage = lazy(() =>
  import('./pages/TechniciansPage').then((m) => ({ default: m.TechniciansPage })),
);
const SitesPage = lazy(() => import('./pages/SitesPage').then((m) => ({ default: m.SitesPage })));
const AssignmentsPage = lazy(() =>
  import('./pages/AssignmentsPage').then((m) => ({ default: m.AssignmentsPage })),
);
const AlertsPage = lazy(() =>
  import('./pages/AlertsPage').then((m) => ({ default: m.AlertsPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const SelfReviewPage = lazy(() =>
  import('./pages/SelfReviewPage').then((m) => ({ default: m.SelfReviewPage })),
);
const UsersPage = lazy(() =>
  import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const AuditPage = lazy(() =>
  import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const SessionsPage = lazy(() =>
  import('./pages/SessionsPage').then((m) => ({ default: m.SessionsPage })),
);
const AssistantPage = lazy(() =>
  import('./pages/AssistantPage').then((m) => ({ default: m.AssistantPage })),
);
const AcceptInvitePage = lazy(() =>
  import('./pages/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  team_id?: string | null;
  team_name?: string | null;
  is_technician: boolean;
  is_demo: boolean;
  last_login_at?: string | null;
  idle_timeout_minutes?: number;
  ui_timezone?: string;
  dashboard_refresh_seconds?: number;
  max_idle_timeout_minutes?: number;
  monitoring_policy_acknowledged?: boolean;
  monitoring_policy_acknowledged_at?: string | null;
  mfa_email_enabled?: boolean;
  mfa_required_for_login?: boolean;
  mfa_enforce_admin?: boolean;
  allowed_modules?: string[];
}

export type LoginResult =
  | { mfa_required: false }
  | { mfa_required: true; mfa_token: string; detail?: string };

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  resendMfa: (mfaToken: string) => Promise<LoginResult>;
  logout: (reason?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Only true during the initial /api/auth/me bootstrap — never during login/MFA.
  // Toggling this on login unmounted LoginPage (AuthChecker) and wiped MFA state.
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const userData = await api.get<User>('/api/auth/me');
    setUser(userData);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await refreshUser();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshUser]);

  async function login(email: string, password: string): Promise<LoginResult> {
    const res = await api.post<{
      mfa_required?: boolean;
      mfa_token?: string;
      detail?: string;
      access_token?: string;
    }>('/api/auth/login', { email, password });
    if (res.mfa_required && res.mfa_token) {
      return {
        mfa_required: true,
        mfa_token: res.mfa_token,
        detail: res.detail,
      };
    }
    await refreshUser();
    return { mfa_required: false };
  }

  async function verifyMfa(mfaToken: string, code: string) {
    await api.post('/api/auth/mfa/verify', { mfa_token: mfaToken, code });
    await refreshUser();
  }

  async function resendMfa(mfaToken: string): Promise<LoginResult> {
    const res = await api.post<{
      mfa_required?: boolean;
      mfa_token?: string;
      detail?: string;
    }>('/api/auth/mfa/resend', { mfa_token: mfaToken });
    if (res.mfa_token) {
      return {
        mfa_required: true,
        mfa_token: res.mfa_token,
        detail: res.detail,
      };
    }
    return { mfa_required: true, mfa_token: mfaToken };
  }

  async function logout(reason?: string) {
    try {
      await api.post('/api/auth/logout', reason ? { reason } : {});
    } finally {
      setUser(null);
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, login, verifyMfa, resendMfa, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function RouteFallback() {
  return (
    <div className="p-8 text-ink-600" role="status">
      Loading…
    </div>
  );
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (!user) return <Navigate to="/login" />;
  const mods = user.allowed_modules || [];
  if (user.role === 'admin' || mods.includes('dashboard')) return <Navigate to="/dashboard" />;
  if (mods.includes('map')) return <Navigate to="/map" />;
  if (mods.includes('self_review')) return <Navigate to="/self-review" />;
  return <Navigate to="/settings" />;
}

function ProtectedRoute({
  children,
  module,
}: {
  children: JSX.Element;
  module?: string;
}) {
  const { user, loading } = useAuth();

  if (loading) return <RouteFallback />;
  if (!user) return <Navigate to="/login" />;
  const mods = user.allowed_modules || [];
  const home =
    user.role === 'admin' || mods.includes('dashboard')
      ? '/dashboard'
      : mods.includes('map')
        ? '/map'
        : mods.includes('self_review')
          ? '/self-review'
          : '/settings';
  if (module && user.role !== 'admin' && !mods.includes(module)) {
    return <Navigate to={home} />;
  }

  return children;
}

function withLayout(page: JSX.Element) {
  return <Layout>{page}</Layout>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <GlobalProcessing />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route
                path="/login"
                element={
                  <AuthChecker>
                    <LoginPage />
                  </AuthChecker>
                }
              />
              <Route
                path="/accept-invite"
                element={
                  <AuthChecker>
                    <AcceptInvitePage />
                  </AuthChecker>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <AuthChecker>
                    <ResetPasswordPage />
                  </AuthChecker>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute module="users">
                    {withLayout(<UsersPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions"
                element={
                  <ProtectedRoute module="sessions">
                    {withLayout(<SessionsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assistant"
                element={
                  <ProtectedRoute module="assistant">
                    {withLayout(<AssistantPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute module="audit">
                    {withLayout(<AuditPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute module="dashboard">
                    {withLayout(<DashboardPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technicians"
                element={
                  <ProtectedRoute module="technicians">
                    {withLayout(<TechniciansPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute module="map">
                    {withLayout(<MapPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sites"
                element={
                  <ProtectedRoute module="sites">
                    {withLayout(<SitesPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assignments"
                element={
                  <ProtectedRoute module="assignments">
                    {withLayout(<AssignmentsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute module="alerts">
                    {withLayout(<AlertsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/time-off"
                element={
                  <ProtectedRoute module="time_off">
                    {withLayout(<TimeOffPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute module="reports">
                    {withLayout(<ReportsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/self-review"
                element={
                  <ProtectedRoute module="self_review">
                    {withLayout(<SelfReviewPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute module="settings">
                    {withLayout(<SettingsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<HomeRedirect />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AuthChecker({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (user) {
    const mods = user.allowed_modules || [];
    const to =
      user.role === 'admin' || mods.includes('dashboard')
        ? '/dashboard'
        : mods.includes('self_review')
          ? '/self-review'
          : '/settings';
    return <Navigate to={to} />;
  }
  return children;
}
